import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Space as SpaceIcon } from "components/icons/generated";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import { SearchQueryOrderDirection, Space } from "types/generated";
import { convertToTreeStructure, isNodeVisible, search, updateTreeRendering } from "utils/tree";

import { SpaceWithParentAccessLevel } from "../../types";
import { createSpacesWithParentAccessLevel } from "../../utils";
import { FILTERS_ORDER_SETTINGS_KEY } from "../../constants";
import { FilterItemKeys } from "../constants";
import { getSortPredicate } from "../utils";
import SPACES_LIST_CELLS from "./Table/Cells";
import { SPACES_LIST_SORT_CONFIG } from "./Table/constants";
import { SpacesListContext } from "./Table/Context";
import SpacesListRows from "./Table/Rows";
import useSpacesListTableStore from "./Table/useStore";
import useSpacesListTreeColumnBounds from "./Table/useTreeColumnBounds";

const ARIA_LABEL = "Spaces";

export type SpacesListCollapseAnchor = { id: string; cellViewportTop: number };

export type SpacesListGetCollapseAnchor = (id: string) => SpacesListCollapseAnchor | null;

type SpacesTreeGridListProps = {
  spaces: Space[];
  sortBy: FilterItemKeys;
  sortDirection: SearchQueryOrderDirection;
  isRootAdmin: boolean;
  searchQuery?: string;
  canNonRootAdminCreateSpace: boolean;
};

const SpacesTreeGridList = ({
  spaces,
  isRootAdmin,
  sortBy,
  sortDirection,
  searchQuery,
  canNonRootAdminCreateSpace,
}: SpacesTreeGridListProps) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const spacesWithParentAccess: Array<SpaceWithParentAccessLevel> = useMemo(
    () => createSpacesWithParentAccessLevel(spaces),
    [spaces]
  );

  const childCounts = useMemo(
    () =>
      spacesWithParentAccess.reduce<Record<string, number>>((counts, space) => {
        if (space.parentSpace) {
          counts[space.parentSpace] = (counts[space.parentSpace] ?? 0) + 1;
        }
        return counts;
      }, {}),
    [spacesWithParentAccess]
  );

  const { flatList } = useMemo(
    () =>
      convertToTreeStructure<SpaceWithParentAccessLevel>(
        spacesWithParentAccess,
        "id",
        "parentSpace",
        "name",
        getSortPredicate(sortBy, sortDirection, childCounts)
      ),
    [sortBy, sortDirection, spacesWithParentAccess, childCounts]
  );

  const allKeysSet = useMemo(() => new Set(flatList.map((item) => item.id)), [flatList]);

  const [expandedKeys, setExpandedKeys] = useState(allKeysSet);
  const [renderedKeys, setRenderedKeys] = useState(allKeysSet);
  // `undefined` so the first render with an active search query still syncs expand/render keys.
  const [treeKeysSync, setTreeKeysSync] = useState({
    searchQuery: undefined as string | undefined,
    flatList,
    allKeysSet,
  });

  if (
    searchQuery !== treeKeysSync.searchQuery ||
    flatList !== treeKeysSync.flatList ||
    allKeysSet !== treeKeysSync.allKeysSet
  ) {
    const queryChanged = searchQuery !== treeKeysSync.searchQuery;
    setTreeKeysSync({ searchQuery, flatList, allKeysSet });

    if (!searchQuery) {
      setRenderedKeys(allKeysSet);

      if (queryChanged) {
        setExpandedKeys(allKeysSet);
      }
    } else {
      const matchedPaths = search(flatList, searchQuery, ["name", "id"]);
      const { expandedNodes, renderedNodes } = updateTreeRendering(flatList, matchedPaths);

      setRenderedKeys(renderedNodes);

      if (queryChanged) {
        setExpandedKeys(expandedNodes);
      }
    }
  }

  const expandedKeysRef = useRef(expandedKeys);
  const collapseAnchorRef = useRef<SpacesListCollapseAnchor | null>(null);
  const getCollapseAnchorRef = useRef<SpacesListGetCollapseAnchor | null>(null);

  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);

  const visibleNodes = useMemo(
    () => flatList.filter((node) => isNodeVisible(node, renderedKeys, expandedKeys)),
    [expandedKeys, renderedKeys, flatList]
  );

  const treeColumnBounds = useSpacesListTreeColumnBounds(visibleNodes);

  const onToggle = useCallback((key: string) => {
    const isCollapsing = expandedKeysRef.current.has(key);

    // Only collapsing shrinks the list, so only then do we anchor. Capture the toggled row's
    // on-screen position now (while it's still rendered) and re-align to it after the collapse.
    collapseAnchorRef.current = isCollapsing ? (getCollapseAnchorRef.current?.(key) ?? null) : null;

    setExpandedKeys((prev) => {
      const newKeys = new Set(prev);
      if (newKeys.has(key)) {
        newKeys.delete(key);
      } else {
        newKeys.add(key);
      }
      return newKeys;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      expandedKeys,
      onToggle,
      isRootAdmin,
      canNonRootAdminCreateSpace,
      searchQuery,
    }),
    [expandedKeys, onToggle, isRootAdmin, canNonRootAdminCreateSpace, searchQuery]
  );

  return (
    <SpacesListContext.Provider value={contextValue}>
      <Box direction="column" grow="1" fullWidth>
        <TableWrapper ref={tableRef} hasPagination={false}>
          {visibleNodes.length === 0 ? (
            <EmptyState
              title="No results"
              icon={SpaceIcon}
              caption="Try different search."
              announce
            />
          ) : (
            <Table ariaLabel={ARIA_LABEL} role="treegrid">
              <TableSimpleHead
                store={useSpacesListTableStore}
                ariaLabel={ARIA_LABEL}
                sortConfig={SPACES_LIST_SORT_CONFIG}
                filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
                tableRef={tableRef}
                columnWidthBounds={treeColumnBounds}
              />
              <TableBody>
                <SpacesListRows
                  nodes={visibleNodes}
                  store={useSpacesListTableStore}
                  cellPerColumn={SPACES_LIST_CELLS}
                  tableRef={tableRef}
                  collapseAnchorRef={collapseAnchorRef}
                  getCollapseAnchorRef={getCollapseAnchorRef}
                  columnWidthBounds={treeColumnBounds}
                />
              </TableBody>
            </Table>
          )}
        </TableWrapper>
      </Box>
    </SpacesListContext.Provider>
  );
};

export default SpacesTreeGridList;
